import { baseCleanProps } from "common/api/Api";
import { IPresentationData, PresentationRecord, toJSON, toFormData } from "common/records/Presentation";
import { fetcher } from "common/api/Fetcher";
import { List } from "immutable";
import { IMetaPaginationData } from "common/records/MetaPagination";
import { ORG_URL, UrlType } from "common/types";
import { toParams } from "common/records/index/QueryParamsBase";
import { PresentationParamsRecord } from "common/records/index/PresentationParams";

export interface IPresentationIndexResponse {
  presentations: IPresentationData[];
  meta?: { pagination: IMetaPaginationData };
}

export interface IPresentationResponse {
  presentation: IPresentationData;
}

export interface IPresentationListOptions {
  parentId: number;
  presentationIds?: List<number>;
}

const url = "api/v1/presentations";
const urls = {
  org: "api/v1/orgs/:id/presentations",
  job: "api/v1/jobs/:id/presentations",
};

export const load = (id: number): Promise<IPresentationResponse> => {
  const actionUrl = fetcher.joinUrls(url, id.toString());

  return fetcher.get(actionUrl);
};

export const list = (queryParams: PresentationParamsRecord): Promise<IPresentationIndexResponse> => {
  const parameters = toParams(queryParams);

  delete parameters.urlType;
  delete parameters.parentId;

  if (queryParams.includeSlides) {
    parameters["include[]"] = ["slides"];
  }
  delete parameters.includeSlides;

  const actionUrl = urls[queryParams.urlType].replace(":id", queryParams.parentId.toString());

  return fetcher.get(actionUrl, parameters);
};

export const create = (
  id: number,
  presentation: PresentationRecord,
  urlType: UrlType = ORG_URL,
): Promise<IPresentationResponse> => {
  const actionUrl = urls[urlType].replace(":id", id.toString());

  const data: Partial<IPresentationData> = cleanProps(presentation);

  return fetcher.post(actionUrl, { presentation: data });
};

export const upload = (presentation: PresentationRecord): Promise<IPresentationResponse> => {
  if (!presentation.org_id) {
    throw new Error("Presentation must have org_id");
  }
  const actionUrl = urls.org.replace(":id", presentation.org_id.toString());
  return fetcher.post(actionUrl, toFormData(presentation), { formData: true });
};

export const update = (presentation: PresentationRecord): Promise<IPresentationResponse> => {
  const actionUrl = fetcher.joinUrls(url, presentation.id.toString());

  const data: Partial<IPresentationData> = cleanProps(presentation);

  return fetcher.patch<IPresentationResponse>(actionUrl, { presentation: data });
};

export const destroy = (presentation: PresentationRecord): Promise<IPresentationResponse> => {
  if (!presentation.org_id) {
    throw new Error("Presentation must have org_id");
  }

  const actionUrl = fetcher.joinUrls(
    urls.org.replace(":id", presentation.org_id.toString()),
    presentation.id.toString(),
  );
  return fetcher.delete(actionUrl);
};

export const coverImage = (presentation: PresentationRecord): Promise<IPresentationResponse> => {
  if (!presentation.org_id) {
    throw new Error("Presentation must have org_id");
  }
  const actionUrl = fetcher.joinUrls(
    urls.org.replace(":id", presentation.org_id.toString()),
    presentation.id.toString(),
    "cover_image",
  );
  return fetcher.post(actionUrl, toFormData(presentation), { formData: true });
};

export const cleanProps = (presentationRecord: PresentationRecord): Partial<IPresentationData> => {
  let presentation = toJSON(presentationRecord);
  presentation = baseCleanProps(presentation);

  if (!presentation.folder_id || presentation.folder_id === 0) {
    delete presentation.folder_id;
  }

  if (!presentation.file) {
    delete (presentation as any).file;
  }

  if (presentation.dynamic_links) {
    if (presentation.dynamic_links.length > 0) {
      (presentation as any).dynamic_presentation_links_attributes = presentation.dynamic_links;
      delete presentation.slides;
    }
    delete presentation.dynamic_links;
  }

  delete presentation.slide_ids;
  delete (presentation as any).url;
  delete (presentation as any).cover_image;

  return presentation;
};
